import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Tv, Users, Sparkles, Clapperboard, Flame } from "lucide-react";
import { SHOWS, SHOWS_BY_SLUG, MOST_WATCHED, getShowStatus } from "@/data/content";
import { MaskedLine, Reveal, Kicker } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ShowCard from "@/components/ShowCard";
import ShowModal from "@/components/ShowModal";
import NowAiring from "@/components/NowAiring";
import WhereToWatch from "@/components/WhereToWatch";

const FEATURED = ["khude-rannabati", "gane-gane-matir-tane", "its-show-time", "10-e-hazar", "lullu-bhuter-goppo", "midday-movies"];

const MANIFESTO = [
    { n: "01", title: "Engage", text: "Content that captivates and connects — from prime-time quizzes to weekend stories that keep every generation watching." },
    { n: "02", title: "Educate", text: "Programming that adds value to daily life — agriculture, wellness, culture and knowledge woven into entertainment." },
    { n: "03", title: "Entertain", text: "Joy, laughter and quality entertainment for the whole family — movies, music, kids' favourites and more." },
];

const HIGHLIGHTS = [
    { icon: Users, big: "2.5 Cr+", label: "Households Reached", text: "Families across West Bengal, Tripura, Assam and Eastern India through 25+ cable & DTH operators.", span: "md:col-span-2" },
    { icon: Clapperboard, big: "12+", label: "Original Shows", text: "Lifestyle, music, children's favourites and prime-time originals.", span: "" },
    { icon: Sparkles, big: "GEC", label: "General Entertainment Channel", text: "A complete family entertainment destination — diverse programming for every member of the family.", span: "" },
    { icon: Tv, big: "DTH + Cable", label: "Available Everywhere", text: "Watch on Tata Play, SUN Direct, Hathway, SITI and 25+ operators across Eastern India.", span: "md:col-span-2" },
];

function PosterColumns() {
    const cols = [
        [SHOWS[0], SHOWS[3], SHOWS[6], SHOWS[9]],
        [SHOWS[1], SHOWS[4], SHOWS[7], SHOWS[10]],
        [SHOWS[2], SHOWS[5], SHOWS[8], SHOWS[11]],
    ];
    return (
        <div className="hero-mask pointer-events-none absolute inset-y-0 right-0 hidden w-[62%] grid-cols-3 gap-4 px-4 opacity-70 md:grid lg:w-[55%]">
            {cols.map((col, ci) => (
                <div key={ci} className="overflow-hidden">
                    <div className={`flex flex-col gap-4 ${ci % 2 ? "animate-marquee-y-reverse" : "animate-marquee-y"}`}>
                        {[...col, ...col].map((show, i) => (
                            <img
                                key={`${show.slug}-${i}`}
                                src={show.img}
                                alt=""
                                loading="lazy"
                                className="aspect-video w-full rounded-xl border border-white/10 object-cover"
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section ref={ref} className="noise-overlay relative flex min-h-[100svh] items-center overflow-hidden bg-[#0F0F23]">
            <PosterColumns />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F23] via-[#0F0F23]/85 to-[#0F0F23]/30" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0F0F23] to-transparent" />
            <div className="absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-[#C8102E]/15 blur-[130px]" />

            <motion.div style={{ y, opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-32 sm:px-6 sm:pt-36">
                <h1 className="font-display max-w-4xl text-[10vw] font-black uppercase leading-[1.04] tracking-tight text-white sm:text-7xl lg:text-8xl">
                    <MaskedLine delay={0.25}>Bengal's</MaskedLine>
                    <MaskedLine delay={0.38} className="font-serif-display font-normal tracking-normal text-[#C8102E]">
                        Favourite
                    </MaskedLine>
                    <MaskedLine delay={0.51} className="text-white">Entertainment</MaskedLine>
                    <span className="flex items-center gap-5">
                        <MaskedLine delay={0.64}>Channel</MaskedLine>
                        <motion.span
                            data-testid="hero-ribbon"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
                            className="h-[3px] min-w-16 flex-1 origin-left bg-gradient-to-r from-[#C8102E] via-[#C8102E]/50 to-transparent"
                        />
                    </span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.85 }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-[#B9B9D4] sm:text-lg"
                >
                    Engage · Educate · Entertain — bringing joy to 2.5 crore households across Eastern India.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    className="mt-9 flex flex-wrap gap-4"
                >
                    <Link
                        to="/shows"
                        data-testid="hero-explore-shows-btn"
                        className="group inline-flex items-center gap-2 rounded-full bg-[#C8102E] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-[#8F0A20] hover:shadow-xl hover:shadow-[#C8102E]/40"
                    >
                        Explore Our Shows
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                    <Link
                        to="/advertise"
                        data-testid="hero-advertise-btn"
                        className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:border-[#FFA200] hover:text-[#FFA200]"
                    >
                        Advertise With Us
                    </Link>
                </motion.div>
            </motion.div>
        </section>
    );
}

export default function Home() {
    const [selected, setSelected] = useState(null);

    return (
        <div data-testid="home-page">
            <Hero />
            <NowAiring />

            <div className="mt-16 bg-[#C8102E] py-4 sm:mt-20" data-testid="tagline-marquee">
                <Marquee
                    items={Array.from({ length: 6 }, (_, i) => (
                        <span key={i} className="font-display inline-flex items-center gap-8 text-lg font-black uppercase tracking-[0.2em] text-white sm:text-xl">
                            Engage <span className="text-[#FFA200]">✦</span> Educate <span className="text-[#FFA200]">✦</span> Entertain <span className="text-[#FFA200]">✦</span> Rongeen TV <span className="mx-4 text-[#FFA200]">✦</span>
                        </span>
                    ))}
                />
            </div>

            <section className="bg-[#0F0F23] py-24 sm:py-32" data-testid="manifesto-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal>
                        <Kicker>Our Promise</Kicker>
                        <h2 className="font-display mt-4 max-w-3xl text-3xl font-black tracking-tight text-white sm:text-5xl">
                            Three words. <span className="text-[#C8102E]">One channel.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-5 md:grid-cols-3">
                        {MANIFESTO.map((m, i) => (
                            <Reveal key={m.n} delay={i * 0.12}>
                                <div data-testid={`manifesto-${m.n}`} className="gold-border-glow group relative h-full overflow-hidden rounded-3xl bg-[#16162B] p-8 transition-transform duration-500 hover:-translate-y-2">
                                    <span className="font-mono2 text-5xl font-black text-[#FFA200]/25 transition-colors duration-500 group-hover:text-[#FFA200]/60">{m.n}</span>
                                    <h3 className="font-display mt-6 text-2xl font-black uppercase tracking-wide text-white">{m.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-[#9999BB]">{m.text}</p>
                                    <div className="absolute -bottom-10 -right-10 h-28 w-28 rounded-full bg-[#C8102E]/10 blur-2xl transition-all duration-500 group-hover:bg-[#C8102E]/25" />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#F5F5F5] py-24 sm:py-32" data-testid="highlights-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal>
                        <Kicker light>Why Rongeen TV</Kicker>
                        <h2 className="font-display mt-4 max-w-3xl text-3xl font-black tracking-tight text-[#0F0F23] sm:text-5xl">
                            Bengal's entertainment revolution
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-5 md:grid-cols-3">
                        {HIGHLIGHTS.map((h, i) => {
                            const Icon = h.icon;
                            return (
                                <Reveal key={h.label} delay={i * 0.08} className={h.span}>
                                    <div data-testid={`highlight-${i}`} className="group flex h-full flex-col justify-between rounded-3xl border border-black/10 bg-white p-8 transition-all duration-500 hover:border-[#C8102E]/40 hover:shadow-2xl hover:shadow-[#C8102E]/10">
                                        <Icon size={28} className="text-[#C8102E]" />
                                        <div className="mt-10">
                                            <p className="font-display text-4xl font-black tracking-tight text-[#0F0F23] sm:text-5xl">
                                                {h.big}
                                            </p>
                                            <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-[#C8102E]">{h.label}</p>
                                            <p className="mt-3 text-sm leading-relaxed text-[#555566]">{h.text}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-[#0F0F23] py-24 sm:py-32" data-testid="featured-shows-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <Kicker>On Rongeen TV</Kicker>
                            <h2 className="font-display mt-4 text-3xl font-black tracking-tight text-white sm:text-5xl">
                                Featured shows
                            </h2>
                        </div>
                        <Link
                            to="/shows"
                            data-testid="view-all-shows-btn"
                            className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#FFA200] transition-colors hover:text-white"
                        >
                            View All Shows <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {FEATURED.map((slug, i) => (
                            <ShowCard key={slug} show={SHOWS_BY_SLUG[slug]} index={i} onOpen={setSelected} />
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#0F0F23] pb-24 sm:pb-32" data-testid="most-watched-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal className="flex items-center gap-4">
                        <span className="rounded-2xl bg-[#C8102E]/15 p-3 text-[#C8102E]"><Flame size={24} /></span>
                        <div>
                            <Kicker>Viewer Favourites</Kicker>
                            <h2 className="font-display mt-2 text-3xl font-black tracking-tight text-white sm:text-5xl">
                                Most watched programmes
                            </h2>
                        </div>
                    </Reveal>
                    <div className="mt-12 grid gap-4 md:grid-cols-2">
                        {MOST_WATCHED.map((slug, i) => {
                            const show = SHOWS_BY_SLUG[slug];
                            const status = getShowStatus(slug);
                            return (
                                <Reveal key={slug} delay={i * 0.08}>
                                    <button
                                        type="button"
                                        data-testid={`most-watched-${slug}`}
                                        onClick={() => setSelected(show)}
                                        className="group flex w-full items-center gap-5 rounded-2xl border border-white/10 bg-[#16162B] p-4 text-left transition-all duration-300 hover:-translate-y-1 hover:border-[#C8102E]/60 sm:p-5"
                                    >
                                        <span className="font-mono2 text-4xl font-black text-[#C8102E]/40 transition-colors duration-300 group-hover:text-[#C8102E] sm:text-5xl">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <img src={show.img} alt={show.title} loading="lazy" className="h-16 w-28 shrink-0 rounded-lg border border-white/10 object-cover sm:h-20 sm:w-36" />
                                        <span className="min-w-0">
                                            <span className="font-display block truncate text-base font-bold text-white transition-colors group-hover:text-[#FFA200] sm:text-lg">{show.title}</span>
                                            <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-[#9999BB]">{show.category} · {show.time}</span>
                                            {status && (
                                                <span className={`mt-1.5 block text-[11px] font-semibold ${status.type === "live" ? "text-[#E85050]" : status.type === "upcoming" ? "text-[#FFA200]" : "text-[#666688]"}`}>
                                                    {status.label}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <WhereToWatch />

            <section className="noise-overlay relative overflow-hidden bg-gradient-to-r from-[#FFA200] to-[#C8102E] py-20 sm:py-24" data-testid="advertise-banner">
                <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
                    <Reveal>
                        <h2 className="font-display max-w-xl text-3xl font-black tracking-tight text-white sm:text-5xl">
                            Reach millions of Bengali households
                        </h2>
                        <p className="mt-4 max-w-xl text-base text-white/85">
                            Partner with Rongeen TV for impactful advertising across Eastern India's most-watched entertainment channel.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <Link
                            to="/advertise"
                            data-testid="banner-advertise-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-[#0F0F23] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-105"
                        >
                            Advertise With Us <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <ShowModal show={selected} onClose={() => setSelected(null)} />
        </div>
    );
}
